import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { navigate } from '@reach/router'
import { useTranslation } from 'react-i18next'

import { formatDate } from '../i18n'
import api from '../services/api'
import Loader from '../Loader'
import Avatar from '../assets/avatar-md.svg'

const { REACT_APP_API_URL } = process.env

// ── Slot ─────────────────────────────────────────────────────────────────────

const RescheduleSlot = ({ time, day, onSelect, disabled }) => (
  <button
    disabled={disabled}
    onClick={() => onSelect({ day, time })}
    className="text-sm bg-blue-100 p-2 rounded text-blue-600 hover:bg-blue-200 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {time}
  </button>
)

// ── Day ──────────────────────────────────────────────────────────────────────

const RescheduleDay = ({ day, slots, onSelect, disabled }) => (
  <div>
    <div className="text-sm my-1 whitespace-nowrap text-first-up">
      {formatDate(new Date(`${day}T00:00`), 'EEEE dd MMMM')}
    </div>
    <div className="overflow-x-auto space-x-2 flex">
      {Object.keys(slots).sort().map(time => (
        <RescheduleSlot
          key={time}
          time={time}
          day={day}
          onSelect={onSelect}
          disabled={disabled}
        />
      ))}
    </div>
  </div>
)

// ── Availability ──────────────────────────────────────────────────────────────

const RescheduleAvailability = ({ teamID, resourceID, locationID, service, financier, date, onSelect, disabled }) => {
  const { t } = useTranslation()
  const [slots, setSlots] = useState({})
  const [cursor, setCursor] = useState(date)

  const key = ['reschedule/availability', teamID, locationID, resourceID, service, financier, cursor]

  const availability = useQuery(
    key,
    () => api(`/${teamID}/availability`, {
      visibility: 'unlisted',
      team: teamID,
      location: locationID,
      resource: resourceID,
      service,
      financier,
      startsAt: cursor,
      endsAt: new Date(cursor).getTime() + 3 * 24 * 60 * 60 * 1000,
      marketplace: 'cartilla',
      valid: 'cartilla',
      price: 'cartilla',
    }),
    { enabled: !!(teamID && resourceID && cursor && service && financier) }
  )

  const next = () => {
    let candidate = [
      new Date().toLocaleDateString('fr-ca'),
      cursor,
      ...Object.keys(slots || {}),
    ].filter(Boolean).sort().reverse()[0]
    candidate = new Date(`${candidate}T00:00`).getTime() + 24 * 60 * 60 * 1000
    setCursor(new Date(candidate).toLocaleDateString('fr-ca'))
  }

  useEffect(() => {
    setCursor(date)
    setSlots({})
  }, [date, resourceID, locationID, service, financier])

  useEffect(() => {
    setSlots(prev => ({ ...prev, ...availability.data?.slots }))
  }, [availability.data])

  if (!service || !financier) return (
    <div className="text-sm text-yellow-700">
      {t('select_service_financier_filter', 'Para tomar un turno seleccione un servicio y una cobertura de los selectores de arriba')}
    </div>
  )

  return (
    <div>
      <div className="overflow-hidden space-y-5">
        {availability.isLoading && (
          <div className="text-sm text-green-700">
            {t('loading_availability', 'Cargando disponibilidad')}
          </div>
        )}
        {Object.keys(slots).sort().map(day => (
          <RescheduleDay
            key={day}
            day={day}
            slots={slots[day]}
            onSelect={onSelect}
            disabled={disabled}
          />
        ))}
      </div>
      <div className="text-right">
        <button className="mt-4 text-sm text-gray-500" onClick={next}>
          {t('more_days', 'Mas dias')}
        </button>
      </div>
    </div>
  )
}

// ── Profile card ──────────────────────────────────────────────────────────────

const RescheduleProfile = ({ resource, location, team, filters, onSelect, disabled }) => {
  const query = useQuery(
    resource?.id,
    () => api(`/${resource?.id}`),
    { enabled: !resource?.label && !!resource?.id, initialData: resource }
  )

  return (
    <div className="bg-white p-5 shadow-md rounded">
      <div className="flex items-center mb-3">
        <div className="rounded-full overflow-hidden" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
          <img src={query.data?.image || Avatar} className="w-full" alt="" />
        </div>
        <div className="ml-6 flex-1 w-full overflow-hidden">
          <div className="font-medium text-lg text-blue-600">{query.data?.label}</div>
          {query.data?.specialties?.length > 0 && (
            <div className="text-sm text-gray-500 overflow-hidden overflow-ellipsis whitespace-nowrap">
              {query.data.specialties.map(s => s.label).join(', ')}
            </div>
          )}
          {location?.address && (
            <div className="text-sm">{location.address}</div>
          )}
        </div>
      </div>

      <RescheduleAvailability
        teamID={team?.id}
        resourceID={resource?.id}
        locationID={location?.id}
        service={filters.service}
        financier={filters.financier}
        date={filters.date}
        onSelect={onSelect}
        disabled={disabled}
      />
    </div>
  )
}

// ── Filters ───────────────────────────────────────────────────────────────────

const Filters = ({ team, filters, onChange }) => {
  const { t } = useTranslation()

  return (
    <div className="sticky top-0 bg-gray-100 py-3">
      <div className="md:flex md:justify-between space-y-2 md:space-y-0 md:space-x-3 overflow-x-auto">
        {team.services?.length > 1 && (
          <div className="flex-none md:w-1/4 md:flex-1">
            <select
              value={filters.service}
              name="service"
              onChange={onChange}
              className={`${!filters.service ? 'bg-red-200 hover:bg-red-300 focus:border-red-700' : 'bg-white hover:bg-gray-50 focus:border-indigo-300'} focus:outline-none active:outline-none min-w-0 max-w-none rounded-md w-full border-none shadow py-2 font-base text-sm text-black text-left`}
            >
              <option value="">{t(filters.service ? 'all' : 'service', { context: 'filters' })}</option>
              {team.services.map(service => (
                <option key={service.key} value={service.key}>{t(service.key, service.label)}</option>
              ))}
            </select>
          </div>
        )}

        {team.financiers?.length > 1 && (
          <div className="flex-none md:w-1/4 md:flex-1">
            <select
              value={filters.financier}
              name="financier"
              onChange={onChange}
              className={`${!filters.financier ? 'bg-red-200 hover:bg-red-300 focus:border-red-700' : 'bg-white hover:bg-gray-50 focus:border-indigo-300'} focus:outline-none active:outline-none min-w-0 max-w-none rounded-md w-full border-none shadow py-2 font-base text-sm text-black text-left`}
            >
              <option value="">{t(filters.financier ? 'all' : 'financier', { context: 'filters' })}</option>
              {team.financiers.map(financier => (
                <option key={financier.key} value={financier.key}>{t(financier.key, financier.label)}</option>
              ))}
            </select>
          </div>
        )}

        {team.resources?.length > 1 && (
          <div className="flex-none md:w-1/4 md:flex-1">
            <select
              value={filters.resource}
              name="resource"
              onChange={onChange}
              className="min-w-0 max-w-none rounded-md w-full border-none shadow py-2 bg-white font-base text-sm text-black hover:bg-gray-50 focus:outline-none focus:border-indigo-300 active:outline-none text-left"
            >
              <option value="">{t(filters.resource ? 'all' : 'resource', { context: 'filters' })}</option>
              {team.resources.filter(r =>
                (!filters.service || r.services.some(s => s.key === filters.service)) &&
                (!filters.financier || r.financiers.some(f => f.key === filters.financier))
              ).map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>
        )}

        {Object.keys(team.locations || {}).length > 1 && (
          <div className="flex-none md:w-1/4 md:flex-1">
            <select
              value={filters.location}
              name="location"
              onChange={onChange}
              className="min-w-0 max-w-none rounded-md w-full border-none shadow py-2 bg-white font-base text-sm text-black hover:bg-gray-50 focus:outline-none focus:border-indigo-300 active:outline-none text-left"
            >
              <option value="">{t(filters.location ? 'all' : 'location', { context: 'filters' })}</option>
              {Object.entries(team.locations).filter(([locationID]) =>
                team.resources
                  .filter(r => !filters.resource || r.id === filters.resource)
                  .filter(r => r.locations.includes(locationID))
                  .length > 0
              ).map(([locationID, location]) => (
                <option key={locationID} value={locationID}>{location.address}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex-none md:w-1/4 md:flex-1">
          <input
            type="date"
            value={filters.date}
            name="date"
            onChange={onChange}
            className="min-w-0 max-w-none whitespace-nowrap w-full rounded-md border-none shadow py-2 bg-white font-base text-sm text-black hover:bg-gray-50 focus:outline-none focus:border-indigo-300 active:outline-none text-left"
          />
        </div>
      </div>

      {(!filters.service || !filters.financier) && (
        <div className="text-sm text-yellow-700 mt-3 text-center bg-yellow-100 border border-yellow-500 rounded-md p-3">
          {t('select_service_financier_filter', 'Para tomar un turno seleccione un servicio y una cobertura de los selectores de arriba')}
        </div>
      )}
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

const Reschedule = (props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [slotError, setSlotError] = useState(null)
  const [pendingSlot, setPendingSlot] = useState(null)
const [filters, setFilters] = useState({
    service: '',
    financier: '',
    resource: '',
    location: '',
    date: new Date().toLocaleDateString('fr-ca'),
  })
  const [filtersReady, setFiltersReady] = useState(false)

  const eventID = props.eventID
  const token = props.token
  const id = `events/${eventID}`.replace(/(\b\S.+\b)(?=.*\1)/g, '').trim()

  const event = useQuery(
    ['events', eventID, token],
    () => fetch(`${REACT_APP_API_URL}/${id}/${token}`).then(res => res.json()),
    { enabled: !!(eventID && token), initialData: {} }
  )

  const team = useQuery(
    ['team', event.data?.teamID],
    () => api(`/${event.data?.teamID}`),
    { enabled: !!event.data?.teamID }
  )

  useEffect(() => {
    if (filtersReady || !team.data || !event.data?.id) return

    const serviceKey = event.data.service?.key
    const financierKey = event.data.financier?.key
    const resourceId = event.data.resource?.id
    const locationId = event.data.location?.id

    setFilters({
      service: team.data.services?.some(s => s.key === serviceKey) ? serviceKey : '',
      financier: team.data.financiers?.some(f => f.key === financierKey) ? financierKey : '',
      resource: team.data.resources?.some(r => r.id === resourceId) ? resourceId : '',
      location: team.data.locations?.[locationId] ? locationId : '',
      date: new Date().toLocaleDateString('fr-ca'),
    })
    setFiltersReady(true)
  }, [team.data, event.data?.id, filtersReady])

  const onChange = ({ target: { name, value } }) => {
    setFilters(f => ({ ...f, [name]: value }))
  }

  const reschedule = useMutation(
    ({ day, time }) => {
      const teamSlug = event.data.teamID?.replace('teams/', '')
      const selectedResource = team.data.resources?.find(r => r.id === filters.resource)
      const selectedService = team.data.services?.find(s => s.key === filters.service)
      const selectedFinancier = team.data.financiers?.find(f => f.key === filters.financier)
      const selectedLocation = filters.location ? team.data.locations?.[filters.location] : null
      return fetch(
        `${REACT_APP_API_URL}/teams/${teamSlug}/events/${eventID}/${token}/reschedule`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            day,
            time,
            resource: selectedResource ? { id: selectedResource.id, label: selectedResource.label } : undefined,
            service: selectedService ? { key: selectedService.key, label: selectedService.label } : undefined,
            financier: selectedFinancier
              ? { key: selectedFinancier.key, label: selectedFinancier.label }
              : { key: 'pms_financiers:private', label: 'Particular' },
            location: selectedLocation ? { id: selectedLocation.id } : undefined,
          }),
        }
      ).then(async res => {
        const data = await res.json()
        if (!res.ok || data?.error) throw data
        return data
      })
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['events', eventID, token])
        const newID = data.id?.replace('events/', '')
        navigate(`/turnos/${newID}/${data.token}/reprogramado`)
      },
      onError: err => {
        setPendingSlot(null)
        if (err?.error === 'slot_bussy') {
          setSlotError(t('reschedule_slot_busy', 'El horario elegido ya no está disponible. Por favor elegí otro.'))
        } else if (err?.error === 'same_slot') {
          setSlotError(t('reschedule_same_slot', 'Ese ya es el horario actual del turno.'))
        } else if (err?.error === 'not_reschedulable') {
          setSlotError(t('reschedule_reason_past', 'Este turno ya pasó.'))
        } else if (err?.message === 'team_locked') {
          setSlotError(err.reason || t('reschedule_error', 'Ocurrió un error al reprogramar. Por favor intentá de nuevo.'))
        } else {
          setSlotError(t('reschedule_error', 'Ocurrió un error al reprogramar. Por favor intentá de nuevo.'))
        }
      },
    }
  )

  if (!event.data?.id || !team.data || !filtersReady) {
    return <Loader className="mt-5 flex flex-col items-center" />
  }

  const ev = event.data
  const teamSlug = ev.teamID?.replace('teams/', '')
  const now = Date.now()

  const blockReason = (() => {
    if (ev.status === 'cancelled' && ev.rescheduledTo) return t('reschedule_reason_rescheduled', 'Este turno ya fue reprogramado.')
    if (ev.status === 'cancelled') return t('reschedule_reason_cancelled', 'Este turno fue cancelado.')
    if (ev.status === 'fulfilled') return t('reschedule_reason_fulfilled', 'Este turno ya fue atendido.')
    if (ev.status === 'noshow') return t('reschedule_reason_noshow', 'Este turno fue marcado como ausente.')
    if (!['booked', 'pending'].includes(ev.status)) return t('reschedule_not_reschedulable', 'Este turno no puede reprogramarse.')
    if (ev.startsAt < now) return t('reschedule_reason_past', 'Este turno ya pasó.')
    return null
  })()

  if (blockReason) {
    return (
      <div className="px-3 mt-5">
        <div className="bg-yellow-100 text-yellow-800 p-4 rounded text-center text-sm">
          <p className="mb-3">{blockReason}</p>
          <a
            href={`/equipos/${teamSlug}`}
            className="inline-block bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-blue-700"
          >
            {t('reschedule_book_new', 'Tomar un nuevo turno')}
          </a>
        </div>
      </div>
    )
  }

  const handleSelect = ({ day, time }) => {
    setSlotError(null)
    setPendingSlot({ day, time })
  }

  const handleConfirm = () => {
    reschedule.mutate(pendingSlot)
  }

  const handleCancel = () => {
    setPendingSlot(null)
  }

  const profileCards = Object.entries(team.data.locations)
    .filter(([locationID]) => !filters.location || locationID === filters.location)
    .flatMap(([locationID, location]) =>
      team.data.resources
        .filter(r => {
          if (filters.resource && r.id !== filters.resource) return false
          if (filters.financier && !r.financiers.find(f => f.key === filters.financier)) return false
          if (filters.service && !r.services.find(s => s.key === filters.service)) return false
          if (!r.locations.includes(location.id)) return false
          return true
        })
        .map(r => ({ key: `${locationID}-${r.id}`, resource: r, location }))
    )

  const profileCardsNoLocation = team.data.resources
    .filter(r => {
      if (filters.resource && r.id !== filters.resource) return false
      if (filters.financier && !r.financiers.find(f => f.key === filters.financier)) return false
      if (filters.service && !r.services.find(s => s.key === filters.service)) return false
      return r.locations.length === 0
    })
    .map(r => ({ key: r.id, resource: r, location: null }))

  return (
    <div className="px-3">
      <div className="bg-blue-50 border border-blue-200 rounded p-4 mt-3 mb-1">
        <div className="text-xs text-blue-600 font-medium mb-1 uppercase tracking-wide">
          {t('reschedule_current_label', 'Turno actual')}
        </div>
        <div className="text-sm font-medium text-secondary text-first-up">
          {formatDate(new Date(`${ev.day}T${ev.time}`), 'EEEE d MMMM')} · {ev.time}
        </div>
        <div className="text-sm text-secondary/70">{ev.resource?.label}</div>
        {(ev.service?.label || ev.location?.address) && (
          <div className="text-[12px] text-secondary/60 mt-0.5">
            {[ev.service?.label, ev.location?.address].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      <Filters team={team.data} filters={filters} onChange={onChange} />

      {slotError && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded p-3 mb-4 text-sm">
          {slotError}
        </div>
      )}

      <div className="space-y-3 pb-4">
        {[...profileCards, ...profileCardsNoLocation].map(({ key, resource, location }) => (
          <RescheduleProfile
            key={key}
            resource={resource}
            location={location}
            team={team.data}
            filters={filters}
            onSelect={handleSelect}
            disabled={!!pendingSlot}
          />
        ))}
      </div>

      {pendingSlot && (() => {
        const selectedResource = team.data.resources?.find(r => r.id === filters.resource)
        const selectedService = team.data.services?.find(s => s.key === filters.service)
        const selectedFinancier = team.data.financiers?.find(f => f.key === filters.financier)
        const selectedLocation = filters.location ? team.data.locations?.[filters.location] : null

        return (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4 pb-4 sm:pb-0">
            <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
              <h3 className="font-medium text-secondary text-[17px] mb-4">
                {t('reschedule_confirm_title', '¿Confirmás la reprogramación?')}
              </h3>

              <dl className="flex flex-col gap-y-2 mb-6">
                <div className="text-sm font-medium text-secondary text-first-up">
                  {formatDate(new Date(`${pendingSlot.day}T${pendingSlot.time}`), 'EEEE d MMMM')}
                  {' · '}{pendingSlot.time}
                </div>
                {selectedResource && (
                  <div className="text-sm text-secondary/80">{selectedResource.label}</div>
                )}
                {selectedService && (
                  <div className="text-[13px] text-secondary/60">{t(selectedService.key, selectedService.label)}</div>
                )}
                {selectedFinancier && (
                  <div className="text-[13px] text-secondary/60">{t(selectedFinancier.key, selectedFinancier.label)}</div>
                )}
                {selectedLocation && (
                  <div className="text-[13px] text-secondary/60">{selectedLocation.address}</div>
                )}
              </dl>

              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  disabled={reschedule.isLoading}
                  className="flex-1 py-2 rounded-md border border-gray-300 text-sm text-secondary hover:bg-gray-50 disabled:opacity-40"
                >
                  {t('cancel', 'Cancelar')}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={reschedule.isLoading}
                  className="flex-1 py-2 rounded-md bg-blue-600 text-sm text-white font-medium hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center"
                >
                  {reschedule.isLoading
                    ? <Loader size="4" color="white" />
                    : t('confirm', 'Confirmar')}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

export default Reschedule
